import { cva } from "class-variance-authority"
import { CalendarEvent, EventType } from "toptaal-shared"
import z from "zod"

export const MINUTES_BEFORE_EVENT_TO_CHECK_IN = 15

export type IntakeAbsenceStatus = "absent_excused" | "absent_unexcused"

export const LessonAbsenceReasonEnum = z.enum([
  "sick",
  "sick_family_member",
  "maternity_leave",
  "religious_holidays",
  "wedding_or_partnership",
  "funeral",
  "medical_appointment",
  "family_reunion",
  "client_manager_permission",
  "government_appointment",
  "other",
])

export type LessonAbsenceReason = z.infer<typeof LessonAbsenceReasonEnum>

export const IntakeAbsenceStatusOptions: {
  label: string
  value: IntakeAbsenceStatus
}[] = [
  { label: "Afwezig met afmelding", value: "absent_excused" },
  { label: "Afwezig zonder afmelding", value: "absent_unexcused" },
]

export const LessonAbsenceReasonOptions: {
  label: string
  value: LessonAbsenceReason
}[] = [
  { label: "Cursist ziek", value: "sick" },
  { label: "Ziek gezinslid", value: "sick_family_member" },
  { label: "Zwangerschaps- en bevallingsverlof", value: "maternity_leave" },
  { label: "Religieuze feestdagen", value: "religious_holidays" },
  {
    label: "Huwelijk of geregistreerd partnerschap",
    value: "wedding_or_partnership",
  },
  { label: "Overlijden/uitvaart", value: "funeral" },
  {
    label: "Medische specialist ziekenhuisafspraak",
    value: "medical_appointment",
  },
  { label: "Gezinshereniging", value: "family_reunion" },
  {
    label: "Afwezig met toestemming klantmanager",
    value: "client_manager_permission",
  },
  {
    label: "Afspraak bij overheidsinstelling",
    value: "government_appointment",
  },
  { label: "Overig", value: "other" },
]

export const LessonAbsenceReasonMap: Record<LessonAbsenceReason, string> = {
  sick: "Cursist ziek",
  sick_family_member: "Ziek gezinslid",
  maternity_leave: "Zwangerschaps- en bevallingsverlof",
  religious_holidays: "Religieuze feestdagen",
  wedding_or_partnership: "Huwelijk of geregistreerd partnerschap",
  funeral: "Overlijden/uitvaart",
  medical_appointment: "Medische specialist ziekenhuisafspraak",
  family_reunion: "Gezinshereniging",
  client_manager_permission: "Afwezig met toestemming klantmanager",
  government_appointment: "Afspraak bij overheidsinstelling",
  other: "Overig",
}

export const eventTypeLabels: Record<EventType, string> = {
  intake: "intake",
  lesson: "les",
}

export const dayEventVariants = cva("font-bold rounded p-2 text-xs", {
  variants: {
    variant: {
      lesson:
        "bg-lightest-grey text-primary-dark-blue border-l-secondary-yellow border-l-3",
      intake:
        "bg-lightest-grey text-primary-dark-blue border-l-secondary-cobalt-green border-l-3",
      exam: "bg-lightest-grey text-primary-dark-blue border-l-secondary-mint-green border-l-3",
      availability:
        "bg-primary-light-green text-primary-dark-blue border-primary-green border",
    },
  },
  defaultVariants: {
    variant: "lesson",
  },
})

export const badgeVariantColors = cva("", {
  variants: {
    variant: {
      lesson: "bg-secondary-light-yellow border-secondary-light-yellow border",
      intake: "bg-secondary-light-cobalt-green border-none",
      exam: "bg-primary-light-green border-primary-light-green border",
      availability: "bg-primary-green/10 border-primary-green",
    },
  },
  defaultVariants: {
    variant: "lesson",
  },
})

export const badgeVariantListView = cva("", {
  variants: {
    variant: {
      lesson: "bg-secondary-light-yellow border-secondary-light-yellow border",
      intake: "bg-secondary-light-cobalt-green border-none",
      cancelled: "bg-light-red border text-red",
      past: "bg-grey border-grey border text-dark-blue",
    },
  },
})

export const borderVariantListView = cva("", {
  variants: {
    variant: {
      lesson: "border-l-secondary-yellow",
      intake: "border-l-secondary-cobalt-green",
      cancelled: "border-l-red",
      past: "border-l-grey",
    },
  },
})

export const renderBadgeLabel = (eventType: CalendarEvent["eventType"]) => {
  switch (eventType) {
    case "intake":
      return "Intake"
    case "lesson":
      return "Les"
    case "exam":
      return "Toetsen"
    default:
      return "Les"
  }
}
